import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { AlertTriangle, Mic, Pause, Play, Send, Square, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useAudioRecorder } from "@/hooks/useAudioRecorder";

/**
 * Payload entregue ao pai quando o usuário confirma o envio. O widget é PURO:
 * não faz upload nem conhece storage — quem recebe (chat/atendimentos/Portal)
 * é que sobe o blob (stories F3/C2/A3/A4).
 */
export interface VoiceRecorderPayload {
  blob: Blob;
  mime: string;
  durationSec: number;
  sizeBytes: number;
  caption?: string;
}

export interface VoiceRecorderProps {
  onSend: (payload: VoiceRecorderPayload) => void;
  onCancel?: () => void;
  /** teto de duração em segundos (default 180 = 3 min). */
  maxDurationSec?: number;
  disabled?: boolean;
  className?: string;
  /** dispara quando entra/sai do modo ATIVO (gravando ou preview). O pai pode
   *  esconder os próprios controles pra o gravador tomar a barra sem sobrepor
   *  (ex.: o composer do chat esconde textarea/anexo/enviar enquanto grava). */
  onActiveChange?: (active: boolean) => void;
}

function formatDuration(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

/** Waveform ao vivo — lê o AnalyserNode do hook e desenha via requestAnimationFrame. */
function LiveWaveform({
  analyserRef,
  active,
}: {
  analyserRef: MutableRefObject<AnalyserNode | null>;
  active: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const analyser = analyserRef.current;
    const data = new Uint8Array(analyser ? analyser.fftSize : 2);

    const draw = () => {
      rafRef.current = requestAnimationFrame(draw);
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);
      ctx.lineWidth = 2;
      ctx.strokeStyle = "rgb(239 68 68)";
      const a = analyserRef.current;
      if (!a) {
        ctx.beginPath();
        ctx.moveTo(0, h / 2);
        ctx.lineTo(w, h / 2);
        ctx.stroke();
        return;
      }
      a.getByteTimeDomainData(data);
      ctx.beginPath();
      const slice = w / data.length;
      let x = 0;
      for (let i = 0; i < data.length; i++) {
        const v = data[i] / 128.0;
        const y = (v * h) / 2;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
        x += slice;
      }
      ctx.lineTo(w, h / 2);
      ctx.stroke();
    };
    draw();

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [active, analyserRef]);

  return (
    <canvas
      ref={canvasRef}
      width={200}
      height={40}
      className="h-10 w-40 max-w-full"
      aria-hidden="true"
    />
  );
}

export function VoiceRecorder({
  onSend,
  onCancel,
  maxDurationSec = 180,
  disabled = false,
  className,
  onActiveChange,
}: VoiceRecorderProps) {
  const {
    status,
    durationSec,
    error,
    recording,
    isSupported,
    analyserRef,
    start,
    stop,
    cancel,
    reset,
  } = useAudioRecorder({
    maxDurationSec,
    onMaxDuration: () =>
      toast.warning(`Limite de ${Math.round(maxDurationSec / 60)} min atingido.`),
  });

  const [caption, setCaption] = useState("");
  const [deniedModalOpen, setDeniedModalOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [cancelArmed, setCancelArmed] = useState(false);

  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const lastPointerTypeRef = useRef<string>("mouse");
  const holdingRef = useRef(false);
  const holdStartYRef = useRef(0);

  const noMic = !isSupported || status === "no-device";
  const isRecording = status === "recording" || status === "requesting";
  const isPreview = status === "stopped" && recording !== null;
  const active = isRecording || isPreview;

  // Avisa o pai quando entra/sai do modo ativo — pra ele ceder a barra ao
  // gravador e não deixar os próprios controles sobreporem (overlap no desktop).
  useEffect(() => {
    onActiveChange?.(active);
  }, [active, onActiveChange]);

  useEffect(() => {
    if (status === "denied") setDeniedModalOpen(true);
  }, [status]);

  useEffect(() => {
    if (status === "error" && error) toast.error(error);
  }, [status, error]);

  const beginRecording = useCallback(() => {
    if (disabled || noMic) return;
    if (status === "denied") {
      setDeniedModalOpen(true);
      return;
    }
    void start();
  }, [disabled, noMic, status, start]);

  // Mouse: clique inicia.
  const handleMicClick = useCallback(() => {
    if (lastPointerTypeRef.current === "touch") return; // gesto touch já tratou
    if (status === "idle" || status === "error") beginRecording();
    else if (status === "denied") setDeniedModalOpen(true);
  }, [status, beginRecording]);

  // Touch: segurar pra gravar; arrastar pra cima arma o cancelamento.
  const handleMicPointerDown = useCallback(
    (e: ReactPointerEvent) => {
      lastPointerTypeRef.current = e.pointerType || "mouse";
      if (e.pointerType !== "touch") return;
      if (disabled || noMic) return;
      if (status === "denied") {
        setDeniedModalOpen(true);
        return;
      }
      e.preventDefault();
      holdingRef.current = true;
      holdStartYRef.current = e.clientY;
      setCancelArmed(false);
      try {
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      } catch {
        /* noop */
      }
      if (status === "idle" || status === "error") beginRecording();
    },
    [disabled, noMic, status, beginRecording]
  );

  const handleMicPointerMove = useCallback((e: ReactPointerEvent) => {
    if (!holdingRef.current) return;
    const draggedUp = holdStartYRef.current - e.clientY;
    setCancelArmed(draggedUp > 60);
  }, []);

  const handleMicPointerUp = useCallback(
    (e: ReactPointerEvent) => {
      if (!holdingRef.current) return;
      holdingRef.current = false;
      try {
        (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
      } catch {
        /* noop */
      }
      if (cancelArmed) {
        setCancelArmed(false);
        cancel();
        onCancel?.();
      } else {
        stop();
      }
    },
    [cancelArmed, cancel, stop, onCancel]
  );

  const handleStop = useCallback(() => stop(), [stop]);
  const handleCancel = useCallback(() => {
    cancel();
    onCancel?.();
  }, [cancel, onCancel]);

  const handleSend = useCallback(() => {
    if (!recording) return;
    onSend({
      blob: recording.blob,
      mime: recording.mimeType,
      durationSec: recording.durationSec,
      sizeBytes: recording.sizeBytes,
      caption: caption.trim() || undefined,
    });
    setCaption("");
    setIsPlaying(false);
    reset();
  }, [recording, caption, onSend, reset]);

  const handleReRecord = useCallback(() => {
    setCaption("");
    setIsPlaying(false);
    reset();
  }, [reset]);

  const handleDiscard = useCallback(() => {
    setCaption("");
    setIsPlaying(false);
    reset();
    onCancel?.();
  }, [reset, onCancel]);

  const handleRetry = useCallback(() => {
    setDeniedModalOpen(false);
    void start();
  }, [start]);

  const togglePlay = useCallback(() => {
    const el = audioElRef.current;
    if (!el) return;
    if (el.paused) {
      // play() pode rejeitar (src inválido/autoplay) — não deixar o botão
      // travado em "tocando".
      el.play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
    } else {
      el.pause();
      setIsPlaying(false);
    }
  }, []);

  // Sem microfone / navegador sem suporte.
  if (noMic) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={cn("inline-flex", className)}>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled
                aria-label="Microfone indisponível"
              >
                <Mic className="h-5 w-5 opacity-50" />
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>Microfone não disponível neste dispositivo.</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {(status === "idle" || status === "denied" || status === "error") && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant={status === "denied" ? "outline" : "ghost"}
                size="icon"
                disabled={disabled}
                aria-label={status === "denied" ? "Permitir o microfone" : "Gravar áudio"}
                onPointerDown={handleMicPointerDown}
                onPointerMove={handleMicPointerMove}
                onPointerUp={handleMicPointerUp}
                onClick={handleMicClick}
              >
                <Mic className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {status === "denied"
                ? "Permita o microfone para gravar áudio."
                : "Toque para gravar (ou segure no celular)."}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {isRecording && (
        <div className="flex items-center gap-2 rounded-md border px-2 py-1">
          <span className="relative flex h-2 w-2" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
          </span>
          <LiveWaveform analyserRef={analyserRef} active={status === "recording"} />
          <span className="tabular-nums text-sm" aria-label="Tempo de gravação">
            {formatDuration(durationSec)}
          </span>
          <Button
            type="button"
            variant={cancelArmed ? "destructive" : "ghost"}
            size="icon"
            aria-label="Cancelar gravação"
            onClick={handleCancel}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="default"
            size="icon"
            aria-label="Parar gravação"
            onClick={handleStop}
          >
            <Square className="h-4 w-4" />
          </Button>
        </div>
      )}

      {isPreview && recording && (
        <div className="flex w-full flex-col gap-2 rounded-md border p-2">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={isPlaying ? "Pausar" : "Tocar"}
              onClick={togglePlay}
            >
              {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </Button>
            <span className="tabular-nums text-sm">{formatDuration(recording.durationSec)}</span>
            <audio
              ref={audioElRef}
              src={recording.url}
              onEnded={() => setIsPlaying(false)}
              onPause={() => setIsPlaying(false)}
              onPlay={() => setIsPlaying(true)}
              className="hidden"
            />
            <div className="ml-auto flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Descartar"
                onClick={handleDiscard}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={handleReRecord}>
                Regravar
              </Button>
              <Button
                type="button"
                variant="default"
                size="sm"
                aria-label="Enviar áudio"
                onClick={handleSend}
              >
                <Send className="mr-1 h-4 w-4" /> Enviar
              </Button>
            </div>
          </div>
          <Textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Legenda (opcional)"
            rows={2}
            aria-label="Legenda do áudio"
          />
        </div>
      )}

      <Dialog open={deniedModalOpen} onOpenChange={setDeniedModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Microfone bloqueado
            </DialogTitle>
            <DialogDescription>
              Para gravar áudio, permita o acesso ao microfone: clique no ícone de
              cadeado/permissões na barra de endereço do navegador, ative o microfone para este site
              e tente novamente.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeniedModalOpen(false)}>
              Entendi
            </Button>
            <Button type="button" onClick={handleRetry}>
              Tentar novamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
